import { generateShortId } from '@o2d/shared';
import { MAX_IMAGE_DIMENSION_PX } from '../../constants/whiteboard-config.constants.js';
import type { ImageObject, ImageSource } from './image.interface.js';

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the ImageObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * World size for a pasted image so it appears at its NATIVE pixel size, only
 * shrinking (never enlarging) when the bitmap is larger than the viewport —
 * with the aspect ratio preserved.
 *
 * `viewportW/H` are the visible board size in screen pixels; `viewportScale` is
 * the current zoom. `fit` clamps the on-screen footprint to the viewport (≤ 1,
 * so it never upscales); dividing by the zoom converts screen → world so the
 * image reads at the intended screen size at the current zoom.
 */
export function fitImageWorldSize(
  naturalW: number,
  naturalH: number,
  viewportW: number,
  viewportH: number,
  viewportScale: number,
): { width: number; height: number } {
  const w = naturalW > 0 ? naturalW : 1;
  const h = naturalH > 0 ? naturalH : 1;
  const fit = Math.min(1, viewportW / w, viewportH / h);
  const scale = viewportScale > 0 ? viewportScale : 1;
  return { width: (w * fit) / scale, height: (h * fit) / scale };
}

/**
 * Square placeholder used when the natural size is unknown (image failed to
 * decode). Kept independent of zoom so the on-screen footprint is stable.
 */
export function fallbackImageSize(viewportScale: number): { width: number; height: number } {
  const side = MAX_IMAGE_DIMENSION_PX / (viewportScale > 0 ? viewportScale : 1);
  return { width: side, height: side };
}

/**
 * Build a fresh image default payload with an explicit world `size` (already
 * aspect-correct — see {@link fitImageWorldSize}). The image is centred on
 * `point` (top-left = point − size/2).
 *
 * `source` carries EXACTLY ONE of `src` / `dataUrl` (see {@link ImageSource});
 * only that field is spread onto the payload, so the other stays ABSENT rather
 * than present-but-undefined.
 */
export function createImageDefaults(
  point: { x: number; y: number },
  source: ImageSource,
  size: { width: number; height: number },
): ImageObject {
  const { width, height } = size;
  const image: ImageObject = {
    id: generateShortId(),
    type: 'image',
    x: point.x - width / 2,
    y: point.y - height / 2,
    width,
    height,
    rotation: 0,
    ...('src' in source ? { src: source.src } : { dataUrl: source.dataUrl }),
  };
  return freezeInDev(image);
}
